// Formulaires de saisie : étude, persona, insight, import de transcription.
//
// Le formulaire d'INSIGHT est le plus important du produit : il impose de citer
// au moins un fait. Ce n'est pas une contrainte d'interface — le serveur refuse
// en 422 `NO_EVIDENCE` et ne laisse aucun insight orphelin en base. L'interface
// se contente de rendre la règle compréhensible AVANT l'envoi.
import { useState, type FormEvent, type ReactNode } from 'react';
import {
  useAddTriangulationRow,
  useCreateAudit,
  useDeleteTest,
  useTestDeletionSummary,
  useCreateInsight,
  useCreatePersona,
  useDeletePersona,
  usePersonaDeletionSummary,
  useCreateStudy,
  useCreateTest,
  useCreateTriangulation,
  useIngestTranscript,
  useStudies,
  useTests,
  usePersonas,
  useProductContext,
  usePillars,
  useEvidenceSuggestions,
  useTriangulations,
  useUpdateAudit,
  useUpdateInsight,
  useUpdatePersona,
  useUpdateTest,
  useUpdateTriangulationRow,
} from '../api/queries';
import { ApiRequestError } from '../api/client';
import { PERSONA_STATUSES, type PersonaStatus } from '../api/types';
import { personaAside, personaTitle } from '../personas/labels';
import type {
  Audit,
  Concordance,
  Cost,
  FeaturePatch,
  Impact,
  Insight,
  Persona,
  PersonaNeed,
  ProblemType,
  RecoKind,
  Seniority,
  TestDeletionSummary,
  TriangulationRow,
  UserTest,
} from '../api/types';
import { Button, ErrorBanner, Eyebrow, Field, IconButton, MicButton, Note, Select } from '../components/ui';
import { appendDictated, useDictation } from '../voice/useDictation';
import { Drawer } from './Drawer';
import { PersonaNeedsEditor, SENIORITIES } from '../personas/PersonaNeeds';
import { COSTS, IMPACTS, PROBLEM_TYPES, RECO_KINDS } from '../insights/labels';
import { sourcePath } from '../sources/StudySources';
import { useRouter } from '../router';
import './write.css';

// --- Champ multiligne (le composant `Field` ne gère que les `input`) --------

export function TextArea({
  label,
  value,
  onChange,
  rows = 4,
  placeholder,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  hint?: string;
}) {
  // Ici la valeur ET son émetteur sont connus : la dictée s'ajoute au texte
  // courant sans détour par l'événement natif (contrairement à `Field`, dont
  // l'API est celle d'un `<input>` standard).
  const dictation = useDictation((text) => onChange(appendDictated(value, text)));

  return (
    // `<label>` enveloppant : cliquer l'étiquette place le curseur dans la zone
    // de texte. Le bouton micro est donc sorti de cette portée — sinon un clic
    // dessus déclencherait aussi la mise au point du champ.
    <div className="zy-field">
      <label className="zy-field__label" htmlFor={`ta-${label}`}>
        {label}
      </label>
      <div className={dictation.supported ? 'zy-input-groupe' : undefined}>
        <textarea
          id={`ta-${label}`}
          className="zy-input"
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <MicButton dictation={dictation} />
      </div>
      {dictation.error ? <span className="zy-field__erreur">{dictation.error}</span> : null}
      {hint ? <span className="zy-field__aide">{hint}</span> : null}
    </div>
  );
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError) return cause.message;
  return 'Une erreur inattendue est survenue.';
}

/** Coquille commune : soumission, état d'attente, erreur, bouton. */
export function FormShell({
  onSubmit,
  submitting,
  error,
  submitLabel,
  disabled,
  children,
}: {
  onSubmit: (e: FormEvent) => void;
  submitting: boolean;
  error: unknown;
  submitLabel: string;
  disabled: boolean;
  children: ReactNode;
}) {
  return (
    <form className="write-form" onSubmit={onSubmit} noValidate>
      {children}
      {error ? <ErrorBanner>{errorMessage(error)}</ErrorBanner> : null}
      <Button type="submit" variant="accent" loading={submitting} disabled={disabled}>
        {submitLabel}
      </Button>
    </form>
  );
}

// --- Étude -----------------------------------------------------------------

// Suggestions courantes pour le type d'étude — pas une liste fermée (le champ
// reste du texte libre via <datalist>) : les méthodes de recherche UX ne
// tiennent pas dans une énumération, mais la plupart des études tombent dans
// ces quelques cases, et « Audit UX » y a sa place au même titre que les
// autres — ce n'est plus un onglet à part, juste un type d'étude parmi d'autres.
const STUDY_TYPES = [
  'Entretiens', 'Observation terrain', 'Test utilisateur', 'Questionnaire', 'Audit UX',
];

export function NewStudyForm({
  projectId,
  onDone,
  originTest,
}: {
  projectId: string;
  onDone: () => void;
  /** Test dont cette étude découle. Pré-alimente la saisie ET conserve le lien :
   *  la fiche du test listera l'étude, l'étude nommera le test. */
  originTest?: { id: string; code: string | null; objective: string | null; target: string | null };
}) {
  const [objective, setObjective] = useState('');
  const [type, setType] = useState('');
  const [method, setMethod] = useState('');
  const [target, setTarget] = useState(originTest?.target ?? '');
  const [context, setContext] = useState(
    originTest ? `Suite du test ${originTest.code ?? originTest.id} : ${originTest.objective ?? ''}`.trim() : '',
  );
  const [period, setPeriod] = useState('');
  const create = useCreateStudy(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!objective.trim()) return;
    try {
      await create.mutateAsync({
        objective, type, method, target, context, period,
        ...(originTest ? { originTestId: originTest.id } : {}),
      });
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={create.isPending}
      error={create.error}
      submitLabel="Créer l’étude"
      disabled={!objective.trim()}
    >
      <TextArea
        label="Objectif *"
        value={objective}
        onChange={setObjective}
        rows={3}
        placeholder="Comprendre pourquoi les techniciens doublent au papier en zone blanche"
        hint="Ce que vous cherchez à comprendre, pas ce que vous allez faire."
      />
      <Field
        label="Type d’étude"
        value={type}
        onChange={(e) => setType(e.target.value)}
        placeholder="Entretiens"
        list="study-types"
        hint="Suggestions ci-dessous, ou tapez la vôtre."
      />
      <datalist id="study-types">
        {STUDY_TYPES.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <Field label="Méthode" value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Entretiens semi-directifs" />
      <Field label="Cible" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="Techniciens en secteur peu couvert" />
      <Field label="Période" value={period} onChange={(e) => setPeriod(e.target.value)} placeholder="Sem. 2–4" />
      <TextArea label="Contexte" value={context} onChange={setContext} rows={3} />
      {originTest ? (
        <Note>
          Cette étude gardera le lien vers le test {originTest.code ?? originTest.id} — visible des
          deux côtés.
        </Note>
      ) : null}
    </FormShell>
  );
}

// --- Persona ---------------------------------------------------------------

// Un MÊME formulaire crée et modifie : dupliquer les deux, c'est se garantir
// qu'ils divergeront (un champ ajouté à la création, oublié à l'édition). La
// présence de `existing` bascule le mode.
export function NewPersonaForm({
  projectId,
  existing,
  onDone,
}: {
  projectId: string;
  existing?: Persona;
  onDone: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [role, setRole] = useState(existing?.role ?? '');
  const [pole, setPole] = useState(existing?.pole ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [seniority, setSeniority] = useState<Seniority | null>(existing?.seniority ?? null);
  const [needs, setNeeds] = useState<PersonaNeed[]>(existing?.needs ?? []);
  const [status, setStatus] = useState<PersonaStatus>(existing?.status ?? 'Brouillon');
  const [studyIds, setStudyIds] = useState<string[]>(
    (existing?.studies ?? []).map((s) => s.id),
  );
  // Les métiers déclarés dans la mesure 0 : le rôle se choisit parmi eux (ou se
  // tape), et c'est lui qui nourrit la proposition de besoins et d'irritants.
  const context = useProductContext(projectId);
  const create = useCreatePersona(projectId);
  const update = useUpdatePersona(projectId);
  const busy = create.isPending || update.isPending;

  // Le serveur refuse un persona sans étude (422). On le dit AVANT l'envoi
  // plutôt qu'après : même principe que la longueur du mot de passe.
  const canSubmit = name.trim() !== '' && studyIds.length > 0;

  // Une ligne laissée vide ne devient pas un besoin fantôme.
  const cleanNeeds = needs.filter((n) => n.label.trim() !== '');

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          patch: { name, role, pole, description, studyIds, seniority, needs: cleanNeeds, status },
        });
      } else {
        await create.mutateAsync({ name, role, pole, description, studyIds, seniority, needs: cleanNeeds, status });
      }
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={create.error ?? update.error}
      submitLabel={existing ? 'Enregistrer' : 'Créer le persona'}
      disabled={!canSubmit}
    >
      {/* Où en est sa construction : une ébauche, un profil qu'on affine, un
          profil validé sur lequel l'équipe s'appuie. */}
      <label className="zy-field">
        <span className="zy-field__label">Statut</span>
        <Select value={status} onChange={(e) => setStatus(e.target.value as PersonaStatus)}>
          {PERSONA_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </Select>
      </label>
      {/* Le rôle d'abord, comme sur la carte : c'est ce qui qualifie le persona. */}
      <div className="field-row">
        <Field
          label="Rôle / métier"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          placeholder="Magasinier"
          list="persona-roles"
          hint={(context.data?.roles.length ?? 0) > 0 ? 'Les métiers de la mesure 0 sont proposés.' : undefined}
        />
        <label className="zy-field">
          <span className="zy-field__label">Séniorité</span>
          <Select
           
            value={seniority ?? ''}
            onChange={(e) => setSeniority((e.target.value || null) as Seniority | null)}
          >
            <option value="">—</option>
            {SENIORITIES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
        </label>
      </div>
      <datalist id="persona-roles">
        {(context.data?.roles ?? []).map((r) => <option key={r.id} value={r.name} />)}
      </datalist>
      <Field label="Nom *" value={name} onChange={(e) => setName(e.target.value)} placeholder="Karim" />
      <Field label="Pôle" value={pole} onChange={(e) => setPole(e.target.value)} placeholder="Terrain" />
      <TextArea
        label="Description"
        value={description}
        onChange={setDescription}
        rows={4}
        placeholder="Expert terrain, prescripteur informel. Exigeant sur la fiabilité."
      />

      <PersonaNeedsEditor
        projectId={projectId}
        role={role}
        seniority={seniority}
        needs={needs}
        onChange={setNeeds}
      />

      {/* LE point non négociable, comme les preuves d'un insight : un persona
          nomme quelqu'un au nom de qui on parlera en restitution. Il doit dire
          sur quel terrain il repose. */}
      <StudyPicker projectId={projectId} selected={studyIds} onChange={setStudyIds} />
      <p className="zy-texte-muet">
        Un persona se déduit d’un terrain observé. Sans étude rattachée, ce serait un profil
        supposé — et rien ne le distinguerait d’une intuition.
      </p>
    </FormShell>
  );
}

// --- Insight (avec sélection des preuves) ----------------------------------

const SEVERITIES = ['Bloquant', 'Plafond', 'Levier'];

/** Un choix dans une liste fermée, « — » pour « non renseigné ». */
function ClosedSelect<T extends string>({
  label, value, onChange, options,
}: {
  label: string;
  value: T | '';
  onChange: (v: T | '') => void;
  options: { value: T; label: string }[];
}) {
  return (
    <label className="zy-field">
      <span className="zy-field__label">{label}</span>
      <Select value={value} onChange={(e) => onChange(e.target.value as T | '')}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </Select>
    </label>
  );
}

export function NewInsightForm({
  projectId,
  existing,
  onDone,
  defaults,
  testFeatures = [],
  preselectedFeatureIds = [],
}: {
  projectId: string;
  existing?: Insight;
  onDone: () => void;
  /** Amorce de saisie quand l'insight naît d'ailleurs (d'un test, typiquement).
   *  Ce sont des propositions : tout reste modifiable avant l'envoi. */
  defaults?: { title?: string; summary?: string; severity?: string; pillarId?: string };
  /** Fonctionnalités mesurées PROPOSÉES comme preuves — celles du test d'où l'on
   *  vient. Une mesure est une preuve au même titre qu'un verbatim. */
  testFeatures?: { id: string; name: string; score: number; testCode: string | null }[];
  /** Celles cochées d'emblée. Deux propriétés distinctes plutôt qu'une règle
   *  implicite : depuis UNE fonctionnalité on en coche une, depuis le bloc
   *  « Résultats » on n'en coche aucune. */
  preselectedFeatureIds?: string[];
}) {
  const editing = Boolean(existing);
  const [title, setTitle] = useState(existing?.title ?? defaults?.title ?? '');
  const [summary, setSummary] = useState(existing?.summary ?? defaults?.summary ?? '');
  const [recommendation, setRecommendation] = useState(existing?.recommendation ?? '');
  // L'interprétation : pourquoi les utilisateurs se comportent ainsi, ce que
  // cela ouvre, et la nature de ce qu'on recommande.
  const [why, setWhy] = useState(existing?.why ?? '');
  const [opportunity, setOpportunity] = useState(existing?.opportunity ?? '');
  const [recoKind, setRecoKind] = useState<RecoKind | ''>(existing?.recommendation_kind ?? '');
  const [severity, setSeverity] = useState(existing?.severity ?? defaults?.severity ?? '');
  const [pillarId, setPillarId] = useState(existing?.pillar_id ?? defaults?.pillarId ?? '');
  // Qualification du problème : sa nature, ce qu'il rapporterait, ce qu'il coûterait.
  const [problemType, setProblemType] = useState<ProblemType | ''>(existing?.problem_type ?? '');
  const [impact, setImpact] = useState<Impact | ''>(existing?.impact ?? '');
  const [cost, setCost] = useState<Cost | ''>(existing?.cost ?? '');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>(preselectedFeatureIds);

  const pillars = usePillars(projectId);

  // L'ASSISTANT cherche à partir du TITRE tant que l'on n'a rien tapé dans le
  // champ d'affinage. Un seul champ de résultats, donc : soit le titre pilote,
  // soit la saisie manuelle prend le relais. Deux listes concurrentes auraient
  // obligé à choisir laquelle croire.
  const usingTitle = query.trim() === '' && title.trim() !== '';
  const assistantQuery = (usingTitle ? title : query).trim();
  const suggest = useEvidenceSuggestions(projectId, assistantQuery);
  const hits = suggest.data?.suggestions ?? [];

  // On dit COMMENT la détection a été faite. « hybride » rapproche par le sens
  // (« retour au papier » trouve « je note dans mon carnet ») ; « lexical » ne
  // fait que des mots communs. Laisser croire au premier quand on n'a que le
  // second ferait conclure à tort qu'il n'existe aucune preuve.
  const detectionLabel =
    hits.length === 0
      ? null
      : suggest.data?.mode === 'hybride'
        ? `${hits.length} fait(s) rapproché(s) par le sens, pas seulement par les mots.`
        : `${hits.length} fait(s) trouvé(s) par mots-clés (rapprochement par le sens non configuré).`;

  const create = useCreateInsight(projectId);
  const update = useUpdateInsight(projectId);
  const busy = create.isPending || update.isPending;

  const toggle = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleFeature = (id: string) =>
    setSelectedFeatures((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // À la création, au moins une preuve est requise — de l'une OU l'autre nature
  // (le serveur refuse sinon, en 422). À l'édition, les preuves se gèrent par
  // leur route dédiée : on ne les touche pas ici, donc on ne les exige pas.
  const evidenceCount = selected.length + selectedFeatures.length;
  const canSubmit = title.trim() !== '' && (editing || evidenceCount > 0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      if (existing) {
        await update.mutateAsync({
          id: existing.id,
          // « Aucun pilier » et « aucune sévérité » se disent `null`, pas chaîne
          // vide : `pillar_id` porte une clé étrangère qu'une chaîne vide viole.
          patch: {
            title,
            summary,
            why,
            opportunity,
            recommendation,
            // Sans recommandation, sa nature n'a pas de sens.
            recommendationKind: recommendation.trim() ? recoKind || null : null,
            severity: severity || null,
            pillarId: pillarId || null,
            problemType: problemType || null,
            impact: impact || null,
            cost: cost || null,
          },
        });
      } else {
        await create.mutateAsync({
          title,
          summary,
          why,
          opportunity,
          recommendation,
          ...(recommendation.trim() && recoKind ? { recommendationKind: recoKind } : {}),
          ...(severity ? { severity } : {}),
          ...(pillarId ? { pillarId } : {}),
          ...(problemType ? { problemType } : {}),
          ...(impact ? { impact } : {}),
          ...(cost ? { cost } : {}),
          verbatimIds: selected,
          ...(selectedFeatures.length > 0 ? { testFeatureIds: selectedFeatures } : {}),
        });
      }
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  const submitLabel = editing
    ? 'Enregistrer'
    : `Créer l’insight (${evidenceCount} preuve${evidenceCount > 1 ? 's' : ''})`;

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={create.error ?? update.error}
      submitLabel={submitLabel}
      disabled={!canSubmit}
    >
      <Field label="Titre *" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Le mode hors-ligne instable provoque un retour au papier" />
      <TextArea label="Constat" value={summary} onChange={setSummary} rows={3} hint="Ce que les faits établissent." />
      <TextArea
        label="Pourquoi ?"
        value={why}
        onChange={setWhy}
        rows={2}
        hint="Pourquoi les utilisateurs se comportent-ils ainsi ? La cause, la contrainte ou la motivation."
      />
      <TextArea
        label="Opportunité"
        value={opportunity}
        onChange={setOpportunity}
        rows={2}
        hint="Qu’est-ce qui améliorerait leur expérience ?"
      />

      <div className="field-row">
        <label className="zy-field">
          <span className="zy-field__label">Sévérité</span>
          <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
            <option value="">—</option>
            {SEVERITIES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          {severity === 'Plafond' ? (
            <span className="zy-field__aide">
              « Plafond » marque un signal critique : ce pilier plafonnera le score global.
            </span>
          ) : null}
        </label>

        <label className="zy-field">
          <span className="zy-field__label">Pilier concerné</span>
          <Select value={pillarId} onChange={(e) => setPillarId(e.target.value)}>
            <option value="">—</option>
            {(pillars.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </label>
      </div>

      <div className="field-row">
        <ClosedSelect label="Type de problème" value={problemType} onChange={setProblemType} options={PROBLEM_TYPES} />
        <ClosedSelect label="Impact métier" value={impact} onChange={setImpact} options={IMPACTS} />
        <ClosedSelect label="Coût de résolution" value={cost} onChange={setCost} options={COSTS} />
      </div>

      <TextArea
        label="Recommandation"
        value={recommendation}
        onChange={setRecommendation}
        rows={3}
        hint="Une action précise et réalisable : quoi changer, et où."
      />
      <label className="zy-field">
        <span className="zy-field__label">Nature de la recommandation</span>
        <Select
         
          value={recoKind}
          onChange={(e) => setRecoKind(e.target.value as RecoKind | '')}
          disabled={!recommendation.trim()}
        >
          <option value="">—</option>
          {RECO_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </Select>
      </label>

      {/* LE point non négociable du produit — à la CRÉATION. En édition, les
          preuves déjà rattachées se gèrent par leur route dédiée : on ne les
          affiche pas ici pour ne pas laisser croire qu'enregistrer les efface. */}
      {editing ? (
        <Note>
          Les faits déjà cités par cet insight ne sont pas modifiés ici. Enregistrer ne touche que
          son texte, sa sévérité et son pilier.
        </Note>
      ) : (
      <div className="evidence-picker">
        <Eyebrow>Preuves — au moins une est requise</Eyebrow>
        <p className="zy-texte-muet">
          Un insight sans fait cité n’est qu’une opinion. Le serveur refuse de l’enregistrer, et
          c’est ce qui rend vos conclusions opposables. Une preuve, c’est un verbatim{' '}
          <em>ou</em> une fonctionnalité mesurée lors d’un test.
        </p>

        {/* Les mesures d'abord : quand on arrive depuis un test, c'est la preuve
            qu'on a sous la main — la chercher parmi les verbatims serait absurde. */}
        {testFeatures.length > 0 ? (
          <>
            <Eyebrow>Fonctionnalités mesurées de ce test</Eyebrow>
            <ul className="picker-list">
              {testFeatures.map((f) => {
                const on = selectedFeatures.includes(f.id);
                return (
                  <li key={f.id}>
                    <label className={`picker-item${on ? ' picker-item-on' : ''}`}>
                      <input className="zy-check" type="checkbox" checked={on} onChange={() => toggleFeature(f.id)} />
                      <span className="picker-body">
                        <span className="picker-text">
                          {f.name} — {Math.round(f.score)} % de réussite
                        </span>
                        <span className="picker-meta">
                          {[f.testCode, 'fonctionnalité mesurée'].filter(Boolean).join(' · ')}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </>
        ) : null}

        {/* L'ASSISTANT. La requête est le TITRE tant qu'on n'a rien tapé ici :
            on cherche donc sans avoir à chercher, et l'affinage reste possible
            quand le titre ne suffit pas. */}
        <Field
          label="Affiner la détection"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={title.trim() ? 'Laissez vide pour détecter depuis le titre' : 'hors-ligne, gants, synchro…'}
          hint={
            usingTitle
              ? 'Détection en cours à partir du titre. Tapez ici pour chercher autre chose.'
              : undefined
          }
        />

        {detectionLabel ? <p className="zy-texte-muet">{detectionLabel}</p> : null}

        {assistantQuery.length < 4 ? (
          <p className="picker-empty">
            Écrivez le titre de l’insight : les faits qui s’en approchent apparaîtront ici.
          </p>
        ) : suggest.isPending ? (
          <p className="picker-empty">Détection des preuves…</p>
        ) : suggest.isError ? (
          <p className="picker-empty">Détection indisponible.</p>
        ) : hits.length === 0 ? (
          <p className="picker-empty">
            {testFeatures.length > 0
              ? 'Aucun verbatim ne rejoint ce titre — les fonctionnalités mesurées ci-dessus suffisent à étayer cet insight.'
              : 'Aucun verbatim ne rejoint ce titre. Reformulez-le, affinez ci-dessus, ou importez d’abord une transcription.'}
          </p>
        ) : (
          <ul className="picker-list">
            {hits.map((hit) => {
              const on = selected.includes(hit.id);
              const meta = [hit.study, hit.persona ?? hit.speaker, hit.sourceRef, hit.timecode]
                .filter(Boolean)
                .join(' · ');
              return (
                <li key={hit.id}>
                  <label className={`picker-item${on ? ' picker-item-on' : ''}`}>
                    <input className="zy-check" type="checkbox" checked={on} onChange={() => toggle(hit.id)} />
                    <span className="picker-body">
                      <span className="picker-text">« {hit.excerpt} »</span>
                      <span className="picker-meta">
                        {/* La pertinence est AFFICHÉE, pas cachée : l'assistant
                            propose, il ne tranche pas. Voir « 41 % » invite à
                            relire avant de cocher ; ne rien voir laisserait
                            croire que l'ordre vaut jugement. */}
                        <span className="picker-score">{hit.relevance} %</span>
                        {meta ? ` · ${meta}` : ''}
                      </span>
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      )}
    </FormShell>
  );
}

// --- Import de transcription ----------------------------------------------

export function ImportTranscriptForm({
  projectId,
  studyId: fixedStudyId,
  onDone,
}: {
  projectId: string;
  /** Étude d'arrivée imposée — quand on importe DEPUIS la fiche d'une étude.
   *  Sinon, on la choisit dans la liste ci-dessous. */
  studyId?: string;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const [filename, setFilename] = useState('');
  const [studyId, setStudyId] = useState(fixedStudyId ?? '');
  // Découper soi-même (par défaut) ou laisser l'IA extraire les verbatims. Le
  // découpage se fait dans une étude : sans étude, seule l'extraction reste.
  const [mode, setMode] = useState<'manual' | 'auto'>('manual');
  const effectiveMode = studyId ? mode : 'auto';
  const studies = useStudies(projectId);
  const ingest = useIngestTranscript(projectId);
  const { navigate } = useRouter();

  function readFile(file: File) {
    const reader = new FileReader();
    reader.onload = () => {
      setText(String(reader.result ?? ''));
      setFilename(file.name);
    };
    reader.readAsText(file);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      const res = await ingest.mutateAsync({
        text,
        filename,
        kind: filename ? 'transcript_file' : 'paste',
        studyId: studyId || null,
        mode: effectiveMode,
      });
      onDone();
      // Gardée pour être découpée : on l'ouvre tout de suite.
      if (effectiveMode === 'manual' && res.sourceId && studyId) navigate(sourcePath(projectId, studyId, res.sourceId));
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  const result = ingest.data;

  return (
    <FormShell
      onSubmit={submit}
      submitting={ingest.isPending}
      error={ingest.error}
      submitLabel={effectiveMode === 'manual' ? 'Importer et découper' : 'Analyser et verser au projet'}
      disabled={!text.trim()}
    >
      {/* RATTACHEMENT À UNE ÉTUDE.
          Sans lui, les verbatims arrivaient bien dans le projet mais
          n'appartenaient à aucun terrain : la fiche de l'étude restait
          désespérément vide après un import, sans que rien ne dise pourquoi. */}
      {fixedStudyId ? null : (
        <label className="zy-field">
          <span className="zy-field__label">Étude d’origine</span>
          <Select
           
            value={studyId}
            onChange={(e) => setStudyId(e.target.value)}
          >
            <option value="">— Aucune (verbatims non rattachés)</option>
            {(studies.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {[s.code, s.objective].filter(Boolean).join(' · ')}
              </option>
            ))}
          </Select>
          <span className="zy-field__aide">
            C’est ce rattachement qui fait apparaître les verbatims dans « Terrain recueilli », et
            qui dit d’où vient chaque citation.
          </span>
        </label>
      )}

      <label className="zy-field">
        <span className="zy-field__label">Fichier .txt, .vtt ou .srt</span>
        <input
          type="file"
          className="zy-input"
          accept=".txt,.vtt,.srt"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) readFile(file);
          }}
        />
        <span className="zy-field__aide">
          Les horodatages et les noms de locuteurs sont conservés : c’est ce qui rend chaque
          citation vérifiable à la seconde près.
        </span>
      </label>

      <TextArea
        label="…ou collez la transcription"
        value={text}
        onChange={setText}
        rows={10}
        placeholder={'00:01:12.000 --> 00:01:20.000\n<v Karim>Avec les gants je rate une touche sur deux.'}
      />

      {studyId ? (
        <fieldset className="zy-field">
          <legend className="zy-field__label">Les verbatims</legend>
          <label className="check-row">
            <input className="zy-radio" type="radio" name="ingest-mode" checked={mode === 'manual'} onChange={() => setMode('manual')} />
            <span>Je découpe moi-même (recommandé) : je relis la transcription et je surligne les passages qui feront preuve. L’IA peut suggérer des passages.</span>
          </label>
          <label className="check-row">
            <input className="zy-radio" type="radio" name="ingest-mode" checked={mode === 'auto'} onChange={() => setMode('auto')} />
            <span>L’IA extrait les verbatims marquants à ma place.</span>
          </label>
        </fieldset>
      ) : null}

      {result ? (
        <p className="success-note">
          {result.deduped
            ? 'Cette source avait déjà été versée — aucun doublon créé.'
            : `${result.count} verbatim${result.count > 1 ? 's' : ''} extrait${result.count > 1 ? 's' : ''} et versé${result.count > 1 ? 's' : ''} au projet.`}
        </p>
      ) : null}
    </FormShell>
  );
}

// --- Test utilisateur (édition) --------------------------------------------

// Statuts contraints par un CHECK côté base : proposer autre chose ferait
// échouer l'écriture en 400. On s'aligne exactement dessus.
const TASK_STATUS = ['Planifié', 'En cours', 'Terminé'];

function StatusSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="zy-field">
      <span className="zy-field__label">Statut</span>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        {TASK_STATUS.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </Select>
    </label>
  );
}

// Éditeur de fonctionnalités — les RÉSULTATS d'un test, donc le temps 3.
// Le serveur RÉÉCRIT tout le jeu à l'enregistrement (delete + réinsert) : le
// formulaire porte donc TOUJOURS la liste complète.
export function FeaturesEditor({
  features,
  onChange,
  pillars,
}: {
  features: FeaturePatch[];
  onChange: (next: FeaturePatch[]) => void;
  pillars: { id: string; name: string }[];
}) {
  const set = (idx: number, patch: Partial<FeaturePatch>) =>
    onChange(features.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const add = () => onChange([...features, { name: '', score: 0, pillarId: null }]);
  const remove = (idx: number) => onChange(features.filter((_, i) => i !== idx));

  return (
    <fieldset className="feature-editor">
      <legend className="zy-field__label">Fonctionnalités mesurées</legend>
      <p className="zy-texte-muet">
        Le taux de réussite d’une fonctionnalité ne pèse sur une note que si elle est rattachée à un
        pilier. Sans rattachement, elle reste « hors score ».
      </p>
      {features.map((f, idx) => (
        <div key={idx} className="feature-edit-full">
          <input
            className="zy-input"
            value={f.name}
            onChange={(e) => set(idx, { name: e.target.value })}
            placeholder="Saisie hors-ligne"
            aria-label="Nom de la fonctionnalité"
          />
          <input
            className="zy-input feature-score-input"
            type="number"
            min={0}
            max={100}
            value={Number.isFinite(f.score) ? f.score : 0}
            onChange={(e) =>
              set(idx, {
                score: e.target.value === '' ? 0 : Math.max(0, Math.min(100, Number(e.target.value))),
              })
            }
            aria-label="Taux de réussite (%)"
          />
          <Select
           
            value={f.pillarId ?? ''}
            onChange={(e) => set(idx, { pillarId: e.target.value || null })}
            aria-label="Pilier rattaché"
          >
            <option value="">— Hors score</option>
            {pillars.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
          <IconButton icon="close" danger
            className="feature-remove"
            onClick={() => remove(idx)}
            label="Retirer cette fonctionnalité" />
        </div>
      ))}
      <Button variant="ghost" size="s" icon="plus" type="button" className="feature-add" onClick={add}>
        Ajouter une fonctionnalité
      </Button>
    </fieldset>
  );
}

// --- Personas visés --------------------------------------------------------
//
// Rattacher des personas RÉELS du projet, au lieu de décrire la cible en texte
// libre : c'est ce qui permettra plus tard de lire un résultat par persona
// plutôt que par intuition. Le champ « Cible » subsiste pour ce qu'un persona ne
// dit pas (« ceux qui travaillent en zone blanche »).
export function PersonaPicker({
  projectId,
  selected,
  onChange,
}: {
  projectId: string;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const personas = usePersonas(projectId);
  const list = personas.data ?? [];
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <fieldset className="persona-picker">
      <legend className="zy-field__label">Personas visés</legend>
      {personas.isPending ? (
        <p className="zy-texte-muet">Chargement des personas…</p>
      ) : list.length === 0 ? (
        <p className="zy-texte-muet">
          Aucun persona sur ce projet pour l’instant. Vous pourrez les rattacher plus tard depuis
          la fiche du test.
        </p>
      ) : (
        <ul className="persona-choices">
          {list.map((p) => (
            <li key={p.id}>
              <label className={`persona-choice${selected.includes(p.id) ? ' persona-choice-on' : ''}`}>
                <input className="zy-check"
                  type="checkbox"
                  checked={selected.includes(p.id)}
                  onChange={() => toggle(p.id)}
                />
                <span className="persona-choice-name">{personaTitle(p)}</span>
                {personaAside(p) ? <span className="persona-choice-role">{personaAside(p)}</span> : null}
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

/** Choix des études d'un persona. Même forme que `PersonaPicker` : ce sont deux
 *  objets du projet qu'on relie, pas des étiquettes qu'on saisit. */
export function StudyPicker({
  projectId,
  selected,
  onChange,
}: {
  projectId: string;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const studies = useStudies(projectId);
  const list = studies.data ?? [];
  const toggle = (id: string) =>
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <fieldset className="persona-picker">
      <legend className="zy-field__label">Études d’origine — au moins une est requise</legend>
      {studies.isPending ? (
        <p className="zy-texte-muet">Chargement des études…</p>
      ) : list.length === 0 ? (
        <p className="zy-texte-muet">
          Aucune étude sur ce projet. Créez d’abord l’étude sur laquelle ce persona s’appuie : un
          persona qui ne repose sur aucun terrain n’est qu’un profil supposé.
        </p>
      ) : (
        <ul className="persona-choices">
          {list.map((s) => (
            <li key={s.id}>
              <label className={`persona-choice${selected.includes(s.id) ? ' persona-choice-on' : ''}`}>
                <input className="zy-check"
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={() => toggle(s.id)}
                />
                <span className="persona-choice-name">
                  {s.objective ?? s.code ?? s.id}
                </span>
                <span className="persona-choice-role">
                  {[s.code, s.type].filter(Boolean).join(' · ')}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </fieldset>
  );
}

// --- Test utilisateur : TEMPS 1, le cadrage --------------------------------
//
// Ce formulaire ne demande QUE ce qu'on sait au moment où l'on planifie : ce
// qu'on cherche à mesurer, sur quels personas, avec combien de participants,
// quand. Il ne demande NI les questions NI les taux de réussite — les premières
// s'écrivent et se font valider sur la fiche du test (temps 2), les seconds
// n'existent qu'après les sessions (temps 3).
//
// L'ancienne version demandait tout d'un coup, taux de réussite compris : elle
// faisait saisir des résultats au moment où l'on préparait le test.
/** Types de test proposés au cadrage. Une liste courte et fermée : un type
 *  tapé librement (« test de tâches modéré », « test modéré », « modéré »)
 *  ne se compte pas. */
export const TEST_TYPES = ['Test de prototype', 'Autre'] as const;

export function TestForm({
  projectId,
  existing,
  onDone,
  onCreated,
}: {
  projectId: string;
  existing?: UserTest;
  onDone: () => void;
  /** Appelé après une CRÉATION, avec l'identifiant du test : l'appelant
   *  enchaîne sur la fiche, là où se renseignent personas, effectif et scénario. */
  onCreated?: (id: string) => void;
}) {
  // Étape 1 = le strict nécessaire pour ouvrir le test : ce qu'on cherche à
  // trancher, sur quoi, et où il en est. Personas et nombre de participants
  // viennent à l'étape 2, sur la fiche. Cible, période et contexte ne sont plus
  // saisis : les valeurs d'un test ancien restent en base, et affichées.
  const [objective, setObjective] = useState(existing?.objective ?? '');
  const [type, setType] = useState(existing?.type || TEST_TYPES[0]);
  const [status, setStatus] = useState(existing?.status || 'Planifié');
  const [productUrl, setProductUrl] = useState(existing?.product_url ?? '');
  const create = useCreateTest(projectId);
  const update = useUpdateTest(projectId);
  const busy = create.isPending || update.isPending;

  // Un test ancien peut porter un type libre hors liste : on le propose tel
  // quel plutôt que de l'écraser en silence au premier enregistrement.
  const typeOptions: string[] = (TEST_TYPES as readonly string[]).includes(type)
    ? [...TEST_TYPES]
    : [...TEST_TYPES, type];

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!objective.trim()) return;
    const patch = { objective, type, status, productUrl: productUrl.trim() };
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, patch });
        onDone();
      } else {
        const res = await create.mutateAsync(patch);
        onDone();
        onCreated?.(res.test.id);
      }
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={create.error ?? update.error}
      submitLabel={existing ? 'Enregistrer' : 'Créer le test'}
      disabled={!objective.trim()}
    >
      <TextArea
        label="Objectif *"
        value={objective}
        onChange={setObjective}
        rows={2}
        placeholder="Mesurer la réussite de la saisie terrain"
        hint="Ce que le test doit permettre de trancher."
      />
      <div className="field-row">
        <label className="zy-field">
          <span className="zy-field__label">Type de test</span>
          <Select value={type} onChange={(e) => setType(e.target.value)}>
            {typeOptions.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Select>
        </label>
        <StatusSelect value={status} onChange={setStatus} />
      </div>
      <Field
        label="URL du prototype"
        type="url"
        value={productUrl}
        onChange={(e) => setProductUrl(e.target.value)}
        placeholder="figma.com/proto/…"
        hint="Facultatif. Le lien que l’on ouvre devant le participant : Figma, maquette, préproduction."
      />

      {existing ? null : (
        <Note>
          Étape suivante, sur la fiche du test : les personas visés, le nombre de participants, puis le
          scénario.
        </Note>
      )}
    </FormShell>
  );
}

/** Étape 2 du test : les personas visés et le nombre de participants, SANS
 *  repasser par le cadrage.
 *  Le tableau est TOUJOURS envoyé : la convention serveur est « absent
 *  préserve, vide efface », et vider est un geste légitime. */
export function TestPersonasForm({
  projectId,
  test,
  onDone,
}: {
  projectId: string;
  test: UserTest;
  onDone: () => void;
}) {
  const [personaIds, setPersonaIds] = useState<string[]>(test.personas.map((p) => p.id));
  // 0 est la valeur « non renseigné » en base : on affiche un champ vide.
  const [participants, setParticipants] = useState(test.participants ? String(test.participants) : '');
  const update = useUpdateTest(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const n = Number.parseInt(participants, 10);
    try {
      await update.mutateAsync({
        id: test.id,
        patch: { personaIds, participants: Number.isFinite(n) && n > 0 ? n : 0 },
      });
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={update.isPending}
      error={update.error}
      submitLabel="Enregistrer les participants"
      disabled={false}
    >
      <PersonaPicker projectId={projectId} selected={personaIds} onChange={setPersonaIds} />
      <Field
        label="Nombre de participants"
        type="number"
        min={0}
        inputMode="numeric"
        value={participants}
        onChange={(e) => setParticipants(e.target.value)}
        placeholder="8"
        hint="Combien de personnes on prévoit de faire passer."
      />
      <p className="zy-texte-muet">
        Rattacher un persona ne le modifie pas : cela dit seulement qui ce test cherche à observer.
      </p>
    </FormShell>
  );
}

// --- Test utilisateur : TEMPS 3, les résultats -----------------------------
//
// Séparé du cadrage : un taux de réussite ne se connaît qu'après les sessions.
// Il vit sur la fiche du test, sous le guide.
export function TestResultsForm({
  projectId,
  test,
  onDone,
}: {
  projectId: string;
  test: UserTest;
  onDone?: () => void;
}) {
  const [features, setFeatures] = useState<FeaturePatch[]>(
    // `f.score` arrive en CHAÎNE de Postgres (colonne numeric) : on le ramène en
    // nombre ici, sinon le champ `type=number` le rejette et retombe à 0 — ce qui
    // écraserait silencieusement un taux mesuré à l'enregistrement.
    test.features.map((f) => ({ name: f.name, score: Number(f.score) || 0, pillarId: f.pillar_id })),
  );
  const pillars = usePillars(projectId);
  const update = useUpdateTest(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      // On ne verse que les fonctionnalités réellement nommées : une ligne vide
      // laissée par mégarde ne doit pas créer une fonctionnalité fantôme.
      await update.mutateAsync({
        id: test.id,
        patch: { features: features.filter((f) => f.name.trim() !== '') },
      });
      onDone?.();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={update.isPending}
      error={update.error}
      submitLabel="Enregistrer les résultats"
      disabled={false}
    >
      <FeaturesEditor features={features} onChange={setFeatures} pillars={pillars.data ?? []} />
    </FormShell>
  );
}

// --- Audit UX (création / édition) -----------------------------------------

export function AuditForm({
  projectId,
  existing,
  onDone,
}: {
  projectId: string;
  existing?: Audit;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(existing?.title ?? '');
  const [version, setVersion] = useState(existing?.version ?? '');
  const [userJourney, setUserJourney] = useState(existing?.user_journey ?? '');
  const [status, setStatus] = useState(existing?.status || 'Planifié');
  const [auditDate, setAuditDate] = useState(existing?.audit_date ?? '');
  const [context, setContext] = useState(existing?.context ?? '');
  // Catégories d'analyse : un `text[]` côté base. On l'édite comme une liste
  // séparée par des virgules — simple, et sans champ répétable à gérer.
  const [cats, setCats] = useState(existing?.analysis_cats.join(', ') ?? '');
  const create = useCreateAudit(projectId);
  const update = useUpdateAudit(projectId);
  const busy = create.isPending || update.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    const analysisCats = cats
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    const patch = { title, version, userJourney, status, auditDate, context, analysisCats };
    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, patch });
      } else {
        await create.mutateAsync(patch);
      }
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={create.error ?? update.error}
      submitLabel={existing ? 'Enregistrer' : 'Créer l’audit'}
      disabled={!title.trim()}
    >
      <Field label="Titre *" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Audit du parcours de saisie" />
      <div className="field-row">
        <Field label="Version" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="v2.3" />
        <StatusSelect value={status} onChange={setStatus} />
      </div>
      <div className="field-row">
        <Field label="Parcours" value={userJourney} onChange={(e) => setUserJourney(e.target.value)} placeholder="Création d’un compte rendu terrain" />
        <Field label="Date" value={auditDate} onChange={(e) => setAuditDate(e.target.value)} placeholder="2026-05-14" />
      </div>
      <Field
        label="Catégories d’analyse"
        value={cats}
        onChange={(e) => setCats(e.target.value)}
        placeholder="Navigation, Feedback, Charge cognitive"
      />
      <TextArea label="Contexte" value={context} onChange={setContext} rows={3} />
    </FormShell>
  );
}

// --- Verser une mesure du test dans une triangulation ----------------------
//
// Le geste que personne ne pouvait faire : un taux de réussite mesuré existait
// dans le test, et il fallait le RESSAISIR à la main pour qu'il pèse dans un
// croisement. On le verse, en nommant le test comme source.
//
// Formulaire dédié plutôt que `TriangulationRowForm` : le choix du croisement
// cible et l'explication de la conversion /100 → /10 n'ont pas leur place dans
// le formulaire générique d'une mesure.
export function AddToTriangulationForm({
  projectId,
  test,
  feature,
  onDone,
}: {
  projectId: string;
  test: UserTest;
  feature: { id: string; name: string; score: number; pillar_id: string | null };
  onDone: () => void;
}) {
  const triangulations = useTriangulations(projectId);
  const [target, setTarget] = useState('');
  const [newObjective, setNewObjective] = useState(test.objective ?? '');
  const [qualSummary, setQualSummary] = useState('');
  const [concordance, setConcordance] = useState('');
  const createTri = useCreateTriangulation(projectId);
  const addRow = useAddTriangulationRow(projectId);
  const busy = createTri.isPending || addRow.isPending;

  // Une note de triangulation vit sur 10, un taux de réussite sur 100. On
  // convertit ET on l'affiche : un 5,8 apparu sans explication ferait douter.
  const score = Math.round(Number(feature.score) || 0) / 10;
  const creatingNew = target === '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (creatingNew && !newObjective.trim()) return;
    try {
      const triangulationId = creatingNew
        ? (await createTri.mutateAsync({ objective: newObjective })).triangulation.id
        : target;
      await addRow.mutateAsync({
        triangulationId,
        row: {
          pillarLabel: feature.name,
          pillarId: feature.pillar_id,
          score,
          quantSummary: `${Math.round(Number(feature.score) || 0)} % de réussite (${test.participants || '?'} participants)`,
          sourceTool: `Test ${test.code ?? test.id}`,
          qualSummary,
          concordance: (concordance || null) as Concordance | null,
          // La mesure ET sa source dans le même envoi : une mesure dont on ne
          // sait plus d'où elle vient ne se relit pas.
          sourceTestId: test.id,
        },
      });
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={createTri.error ?? addRow.error}
      submitLabel="Ajouter la mesure"
      disabled={creatingNew && !newObjective.trim()}
    >
      <div className="deletion-inventory">
        <p className="zy-field__label">Ce qui sera versé</p>
        <p className="zy-texte-muet">
          <strong>{feature.name}</strong> — {Math.round(Number(feature.score) || 0)} % de réussite,
          converti en <strong>{score.toFixed(1)} / 10</strong> (l’échelle des croisements).
          {feature.pillar_id
            ? ' Le pilier rattaché à la fonctionnalité est repris.'
            : ' Cette fonctionnalité n’est rattachée à aucun pilier : la mesure ne pèsera sur aucune note tant qu’elle ne le sera pas.'}
        </p>
      </div>

      <label className="zy-field">
        <span className="zy-field__label">Croisement cible</span>
        <Select value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">— Nouveau croisement</option>
          {(triangulations.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.objective ?? t.id}
            </option>
          ))}
        </Select>
      </label>

      {creatingNew ? (
        <Field
          label="Objectif du croisement *"
          value={newObjective}
          onChange={(e) => setNewObjective(e.target.value)}
          placeholder="Croiser la réussite mesurée avec les signaux terrain"
        />
      ) : null}

      {/* Ni le signal terrain ni la concordance ne se pré-remplissent : ce sont
          des actes d'analyse, pas des recopies. */}
      <TextArea
        label="Signal terrain"
        value={qualSummary}
        onChange={setQualSummary}
        rows={2}
        hint="Ce que disent les verbatims sur ce point — à vous de l’écrire."
      />
      <label className="zy-field">
        <span className="zy-field__label">Concordance</span>
        <Select value={concordance} onChange={(e) => setConcordance(e.target.value)}>
          <option value="">— À juger</option>
          {CONCORDANCES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </label>
    </FormShell>
  );
}

// --- Triangulation (création du croisement) --------------------------------
//
// La création ne pose que le cadre (objectif + contexte). Les mesures s'ajoutent
// ensuite une à une depuis l'écran, via `TriangulationRowForm` — c'est là que se
// fait le rattachement au pilier.
export function NewTriangulationForm({ projectId, onDone }: { projectId: string; onDone: () => void }) {
  const [objective, setObjective] = useState('');
  const [context, setContext] = useState('');
  const create = useCreateTriangulation(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!objective.trim()) return;
    try {
      await create.mutateAsync({ objective, context });
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={create.isPending}
      error={create.error}
      submitLabel="Créer le croisement"
      disabled={!objective.trim()}
    >
      <TextArea
        label="Objectif *"
        value={objective}
        onChange={setObjective}
        rows={2}
        placeholder="Croiser la saisie terrain et les données d’usage"
        hint="Ce que ce croisement cherche à confronter."
      />
      <TextArea label="Contexte" value={context} onChange={setContext} rows={2} placeholder="Sources : entretiens terrain + Mixpanel" />
    </FormShell>
  );
}

// --- Ligne de triangulation (ajout / édition) ------------------------------

const CONCORDANCES: Concordance[] = ['concordant', 'divergent', 'à creuser'];

// Un même formulaire ajoute une mesure à un croisement ou en modifie une : la
// présence de `existing` bascule le mode, `triangulationId` ne sert qu'à l'ajout.
// Outils d'où sortent le plus souvent les données d'usage. Liste OUVERTE
// (datalist) : chaque produit a son back-office, et figer la liste obligerait à
// mentir sur la provenance faute d'y trouver le bon nom.
const MEASURE_TOOLS = [
  'Google Analytics', 'Matomo', 'Mixpanel', 'Amplitude', 'Hotjar',
  'Back-office produit', 'Export base de données', 'Tickets support',
];

export function TriangulationRowForm({
  projectId,
  triangulationId,
  existing,
  onDone,
}: {
  projectId: string;
  triangulationId: string;
  existing?: TriangulationRow;
  onDone: () => void;
}) {
  const [pillarLabel, setPillarLabel] = useState(existing?.pillar_label ?? '');
  const [pillarId, setPillarId] = useState(existing?.pillar_id ?? '');
  const [score, setScore] = useState(existing?.score != null ? String(existing.score) : '');
  const [qualSummary, setQualSummary] = useState(existing?.qual_summary ?? '');
  const [quantSummary, setQuantSummary] = useState(existing?.quant_summary ?? '');
  const [sourceTool, setSourceTool] = useState(existing?.source_tool ?? '');
  const [concordance, setConcordance] = useState<string>(existing?.concordance ?? '');
  // La donnée réelle, en pièces détachées : c'est ce qui la rend comparable
  // d'une période à l'autre, là où une phrase ne l'était pas.
  const [metricName, setMetricName] = useState(existing?.metric_name ?? '');
  const [metricValue, setMetricValue] = useState(
    existing?.metric_value != null ? String(existing.metric_value) : '',
  );
  const [metricUnit, setMetricUnit] = useState(existing?.metric_unit ?? '');
  const [metricPeriod, setMetricPeriod] = useState(existing?.metric_period ?? '');
  const [sourceStudyId, setSourceStudyId] = useState(existing?.source_study_id ?? '');
  const [sourceTestId, setSourceTestId] = useState(existing?.source_test_id ?? '');

  const pillars = usePillars(projectId);
  const studies = useStudies(projectId);
  const tests = useTests(projectId);
  const add = useAddTriangulationRow(projectId);
  const edit = useUpdateTriangulationRow(projectId);
  const busy = add.isPending || edit.isPending;

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Une mesure non chiffrée reste `null` : `Number('')` vaudrait zéro, et un
    // zéro inventé fausserait la note du pilier.
    const trimmed = score.trim();
    const parsed = trimmed === '' ? null : Number.parseFloat(trimmed);
    const scoreVal = parsed !== null && Number.isFinite(parsed) ? parsed : null;
    // Même règle que la note : une mesure non chiffrée reste `null`.
    // `Number('')` vaudrait zéro, et un zéro inventé se lirait comme un
    // effondrement de la métrique.
    const rawMetric = metricValue.trim();
    const parsedMetric = rawMetric === '' ? null : Number.parseFloat(rawMetric);
    const patch = {
      pillarLabel,
      pillarId: pillarId || null,
      score: scoreVal,
      qualSummary,
      quantSummary,
      sourceTool,
      concordance: (concordance || null) as Concordance | null,
      metricName,
      metricValue: parsedMetric !== null && Number.isFinite(parsedMetric) ? parsedMetric : null,
      metricUnit,
      metricPeriod,
      sourceStudyId: sourceStudyId || null,
      sourceTestId: sourceTestId || null,
    };
    try {
      if (existing) {
        await edit.mutateAsync({ rowId: existing.id, patch });
      } else {
        await add.mutateAsync({ triangulationId, row: patch });
      }
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  return (
    <FormShell
      onSubmit={submit}
      submitting={busy}
      error={add.error ?? edit.error}
      submitLabel={existing ? 'Enregistrer' : 'Ajouter la mesure'}
      disabled={false}
    >
      <label className="zy-field">
        <span className="zy-field__label">Pilier rattaché</span>
        <Select value={pillarId} onChange={(e) => setPillarId(e.target.value)}>
          <option value="">— Aucun</option>
          {(pillars.data ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <span className="zy-field__aide">
          C’est le pilier rattaché qui décide sur quelle note la mesure pèse. Le libellé libre
          ci-dessous n’est qu’un repère de saisie.
        </span>
      </label>
      <div className="field-row">
        <Field label="Libellé (repère)" value={pillarLabel} onChange={(e) => setPillarLabel(e.target.value)} placeholder="Fiabilité hors-ligne" />
        <Field label="Note /10" value={score} onChange={(e) => setScore(e.target.value)} placeholder="laisser vide si non mesuré" />
      </div>
      {/* ---- JAMBE 1 : ce que les gens DISENT ---- */}
      <Eyebrow>Signal terrain — ce qui a été observé</Eyebrow>
      <TextArea label="Signal terrain" value={qualSummary} onChange={setQualSummary} rows={2} placeholder="Les techniciens décrivent des pertes de saisie en zone blanche." />
      <label className="zy-field">
        <span className="zy-field__label">Étude d’origine</span>
        <Select value={sourceStudyId} onChange={(e) => setSourceStudyId(e.target.value)}>
          <option value="">— Non rattachée</option>
          {(studies.data ?? []).map((st) => (
            <option key={st.id} value={st.id}>
              {[st.code, st.objective].filter(Boolean).join(' · ')}
            </option>
          ))}
        </Select>
        <span className="zy-field__aide">
          Sans elle, la ligne affirme un ressenti sans pouvoir dire d’où il sort.
        </span>
      </label>

      {/* ---- JAMBE 2 : ce qu'ils FONT en test ---- */}
      <Eyebrow>Test utilisateur — ce qui a été mesuré en session</Eyebrow>
      <label className="zy-field">
        <span className="zy-field__label">Test d’origine</span>
        <Select value={sourceTestId} onChange={(e) => setSourceTestId(e.target.value)}>
          <option value="">— Non rattaché</option>
          {(tests.data ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {[t.code, t.objective].filter(Boolean).join(' · ')}
            </option>
          ))}
        </Select>
      </label>

      {/* ---- JAMBE 3 : ce que l'USAGE RÉEL montre ---- */}
      <Eyebrow>Donnée réelle — ce que l’usage montre</Eyebrow>
      <div className="field-row">
        <Field label="Métrique" value={metricName} onChange={(e) => setMetricName(e.target.value)} placeholder="Taux d’abandon du formulaire" />
        <Field label="Outil source" value={sourceTool} onChange={(e) => setSourceTool(e.target.value)} placeholder="Google Analytics" list="triangulation-tools" />
      </div>
      <datalist id="triangulation-tools">
        {MEASURE_TOOLS.map((t) => (
          <option key={t} value={t} />
        ))}
      </datalist>
      <div className="field-row">
        <Field
          label="Valeur"
          value={metricValue}
          onChange={(e) => setMetricValue(e.target.value)}
          placeholder="laisser vide si non mesuré"
          inputMode="decimal"
        />
        <Field label="Unité" value={metricUnit} onChange={(e) => setMetricUnit(e.target.value)} placeholder="%" />
        <Field label="Période" value={metricPeriod} onChange={(e) => setMetricPeriod(e.target.value)} placeholder="mars 2026" />
      </div>
      <TextArea
        label="Lecture de la donnée"
        value={quantSummary}
        onChange={setQuantSummary}
        rows={2}
        placeholder="La hausse suit la mise en production de la v3."
        hint="L’interprétation, pas le chiffre : c’est ce qu’un nombre seul ne dit jamais."
      />
      <label className="zy-field">
        <span className="zy-field__label">Concordance</span>
        <Select value={concordance} onChange={(e) => setConcordance(e.target.value)}>
          <option value="">—</option>
          {CONCORDANCES.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </Select>
      </label>
    </FormShell>
  );
}

// --- Déclencheur : bouton + panneau ---------------------------------------

export function WriteAction({
  label,
  title,
  description,
  wide,
  accent,
  children,
}: {
  label: string;
  title: string;
  description?: string;
  wide?: boolean;
  /** Bouton en couleur d'action (cyan) : pour L'action principale d'un écran. */
  accent?: boolean;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={accent ? 'accent' : 'ghost'} onClick={() => setOpen(true)}>
        {label}
      </Button>
      <Drawer
        open={open}
        title={title}
        {...(description ? { description } : {})}
        wide={wide}
        onClose={() => setOpen(false)}
      >
        {children(() => setOpen(false))}
      </Drawer>
    </>
  );
}

// --- Supprimer un test : voir, puis supprimer ------------------------------
//
// Deux temps, mais pas la cérémonie des projets : on ne fait pas retaper
// l'objectif d'un test (une phrase entière) — ce serait une dictée, pas une
// précaution. Ce qui tient lieu de délibération, c'est de LIRE l'inventaire.

/** Libellés singulier / pluriel de l'inventaire. « rattachement de persona » et
 *  jamais « persona » : les personas, eux, survivent. */
const TEST_DELETION_LABELS: [keyof TestDeletionSummary['counts'], string, string][] = [
  ['scenarioSteps', 'action du scénario', 'actions du scénario'],
  ['features', 'fonctionnalité mesurée', 'fonctionnalités mesurées'],
  ['questions', 'question du guide', 'questions du guide'],
  ['personas', 'rattachement de persona', 'rattachements de personas'],
  ['citations', 'citation dans un insight', 'citations dans des insights'],
  ['studies', 'étude qui perdra son lien d’origine', 'études qui perdront leur lien d’origine'],
];

function DeleteTestForm({
  projectId,
  test,
  onDone,
  onDeleted,
}: {
  projectId: string;
  test: UserTest;
  onDone: () => void;
  onDeleted?: () => void;
}) {
  const summary = useTestDeletionSummary(projectId, test.id, true);
  const remove = useDeleteTest(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await remove.mutateAsync({ testId: test.id });
      onDone();
      onDeleted?.();
    } catch {
      /* l'erreur est rendue par la coquille */
    }
  }

  const counts = summary.data?.counts;
  const inventory = counts
    ? TEST_DELETION_LABELS.filter(([key]) => counts[key] > 0).map(
        ([key, one, many]) => `${counts[key]} ${counts[key] > 1 ? many : one}`,
      )
    : [];
  const orphans = counts?.orphanedInsights ?? 0;

  return (
    <FormShell
      onSubmit={submit}
      submitting={remove.isPending}
      error={remove.error}
      submitLabel="Supprimer définitivement"
      disabled={summary.isPending}
    >
      {summary.isPending ? (
        <p className="zy-texte-muet">Inventaire du test en cours…</p>
      ) : summary.isError ? (
        <ErrorBanner>{errorMessage(summary.error)}</ErrorBanner>
      ) : (
        <div className="deletion-inventory">
          <p className="zy-field__label">Ce qui sera détruit</p>
          {inventory.length === 0 ? (
            <p className="zy-texte-muet">Ce test ne contient encore ni scénario ni résultat.</p>
          ) : (
            <ul className="deletion-list">
              {inventory.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {summary.data && summary.data.measured.score !== null ? (
            <p className="deletion-warning">
              Le résultat mesuré ({Math.round(summary.data.measured.score)} % sur{' '}
              {summary.data.measured.count} fonctionnalité
              {summary.data.measured.count > 1 ? 's' : ''}) disparaît avec le test, et avec lui son
              poids sur les piliers d’adoption.
            </p>
          ) : null}
          {/* La seule ligne de l'inventaire qui engage une CONCLUSION, pas des
              objets. On prévient ; on ne supprime pas les insights à sa place. */}
          {orphans > 0 ? (
            <p className="deletion-warning">
              {orphans > 1
                ? `${orphans} insights ne citeront plus aucune preuve. Ils ne sont pas supprimés — ils deviennent non opposables.`
                : '1 insight ne citera plus aucune preuve. Il n’est pas supprimé — il devient non opposable.'}
            </p>
          ) : null}
        </div>
      )}
      <p className="zy-texte-muet">
        La suppression est immédiate et définitive : il n’y a pas de corbeille. Les personas visés et
        les insights, eux, survivent.
      </p>
    </FormShell>
  );
}

/** Déclencheur discret + panneau de confirmation. `onDeleted` sert à la fiche,
 *  qui doit repartir vers la liste quand le test qu'elle affiche disparaît. */
export function DeleteTestAction({
  projectId,
  test,
  onDeleted,
}: {
  projectId: string;
  test: UserTest;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="link-danger" size="s" type="button" onClick={() => setOpen(true)}>
        Supprimer
      </Button>
      <Drawer
        open={open}
        title={`Supprimer « ${test.objective ?? test.code ?? test.id} » ?`}
        description="Voyez ce qui disparaît, puis confirmez."
        onClose={() => setOpen(false)}
      >
        <DeleteTestForm
          projectId={projectId}
          test={test}
          onDone={() => setOpen(false)}
          {...(onDeleted ? { onDeleted } : {})}
        />
      </Drawer>
    </>
  );
}

// --- Suppression d'un persona, en deux temps -------------------------------
//
// Rien n'est DÉTRUIT ici : les verbatims de cette personne survivent et perdent
// seulement leur attribution (`on delete set null`). L'inventaire le dit
// explicitement — « perdront leur locuteur », pas « seront supprimés » — parce
// qu'une confirmation qui laisse croire à une destruction fait renoncer à un
// geste anodin, et fait accepter à la légère celui qui ne l'est pas.
function DeletePersonaForm({
  projectId,
  persona,
  onDone,
}: {
  projectId: string;
  persona: Persona;
  onDone: () => void;
}) {
  const summary = usePersonaDeletionSummary(persona.id, true);
  const del = useDeletePersona(projectId);

  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await del.mutateAsync(persona.id);
      onDone();
    } catch {
      /* l'erreur est rendue par `FormShell` */
    }
  }

  const s = summary.data;
  return (
    <FormShell
      onSubmit={submit}
      submitting={del.isPending}
      error={del.error}
      submitLabel="Supprimer le persona"
      disabled={summary.isPending}
    >
      {summary.isPending ? (
        <p className="zy-texte-muet">Inventaire du persona en cours…</p>
      ) : summary.isError ? (
        <ErrorBanner>{errorMessage(summary.error)}</ErrorBanner>
      ) : s ? (
        <div className="deletion-inventory">
          <p className="zy-field__label">Ce qui change</p>
          <ul className="deletion-list">
            <li>
              {s.verbatims === 0
                ? 'Aucun verbatim ne lui est attribué'
                : s.verbatims === 1
                  ? '1 verbatim — conservé, mais il perdra son attribution'
                  : `${s.verbatims} verbatims — conservés, mais ils perdront leur attribution`}
            </li>
            <li>
              {s.studies === 0
                ? 'Aucune étude rattachée'
                : s.studies === 1
                  ? '1 étude — conservée, simplement déliée'
                  : `${s.studies} études — conservées, simplement déliées`}
            </li>
            <li>
              {s.tests === 0
                ? 'Aucun test ne le vise'
                : s.tests === 1
                  ? '1 test utilisateur — conservé, ce persona n’y figurera plus comme cible'
                  : `${s.tests} tests utilisateurs — conservés, ce persona n’y figurera plus comme cible`}
            </li>
          </ul>
          {s.verbatims > 0 ? (
            <p className="deletion-warning">
              Les citations de cette personne resteront lisibles, mais plus rattachées à un
              persona : elles diront ce qui a été dit, sans dire par qui.
            </p>
          ) : null}
        </div>
      ) : null}
      <p className="zy-texte-muet">
        Pour effacer les données d’une personne au titre du RGPD — verbatims compris — c’est
        l’effacement dédié qu’il faut, pas cette suppression.
      </p>
    </FormShell>
  );
}

export function DeletePersonaAction({
  projectId,
  persona,
}: {
  projectId: string;
  persona: Persona;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="link-danger" size="s" type="button" onClick={() => setOpen(true)}>
        Supprimer
      </Button>
      <Drawer
        open={open}
        title={`Supprimer « ${persona.name} » ?`}
        description="Voyez ce qui change, puis confirmez."
        onClose={() => setOpen(false)}
      >
        <DeletePersonaForm projectId={projectId} persona={persona} onDone={() => setOpen(false)} />
      </Drawer>
    </>
  );
}

// --- Déclencheur d'édition : un lien discret + panneau --------------------
//
// L'édition part d'une carte existante, pas d'un bouton d'action global : le
// déclencheur est donc un lien sobre « Modifier », pas un bouton plein.
export function EditAction({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="link" size="s" className="u-push-end" type="button" onClick={() => setOpen(true)}>
        Modifier
      </Button>
      <Drawer
        open={open}
        title={title}
        {...(description ? { description } : {})}
        onClose={() => setOpen(false)}
      >
        {children(() => setOpen(false))}
      </Drawer>
    </>
  );
}
